import type { JSX } from 'react';

import type { AutomationDetailProjection } from '@/lib/api/automations.js';

/**
 * Labels and small display helpers shared by the automation detail page and
 * its read-only counterpart, so the two pages describe the same automation
 * the same way.
 */

/** Format an ISO timestamp for the "Next run" field, or an em dash when there is none. */
export function formatNextFire(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** One labeled fact in the detail page's info column. */
export function InfoField({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-sm text-foreground">{value}</span>
    </div>
  );
}

/** The label for an automation's content field, by content kind. */
export function automationContentLabel(
  kind: AutomationDetailProjection['content']['kind'],
): string {
  if (kind === 'message') return 'Message';
  if (kind === 'checkin') return 'Check-in message';
  return 'Prompt';
}

/** The recipient line, with the email appended when known. */
export function recipientLabel(
  recipient: AutomationDetailProjection['recipient'],
): string | null {
  if (!recipient) return null;
  return recipient.email
    ? `${recipient.displayName} — ${recipient.email}`
    : recipient.displayName;
}

/** The automation's current state, in the same words on both detail pages. */
export function automationStateLabel(
  automation: AutomationDetailProjection,
): string {
  if (automation.pauseReason === 'max_fires')
    return automation.pendingDeferral
      ? 'Waiting for deferred run'
      : 'Completed';
  if (automation.pauseReason === 'delivery_failure') {
    return 'Paused while delivery recovers';
  }
  if (automation.pauseReason === 'undeliverable') {
    return 'Paused: recipient needs attention';
  }
  return automation.enabled ? 'Active' : 'Paused';
}

/** Label paused or overdue work without a promised date; future runs use the schedule's timezone. */
export function holidayDeferralLabel(
  deferral: NonNullable<AutomationDetailProjection['pendingDeferral']>,
  timezone: string,
  pauseReason: AutomationDetailProjection['pauseReason'],
): string {
  if (pauseReason === 'user') return 'Deferred run paused';
  if (
    deferral.reason !== 'public_holiday' ||
    typeof deferral.until !== 'string'
  )
    return 'Deferred run pending';
  const date = new Date(deferral.until);
  if (!Number.isFinite(date.getTime()) || date.getTime() <= Date.now())
    return 'Deferred run pending';
  const until = date.toLocaleString(undefined, {
    timeZone: timezone,
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  return `Deferred: public holiday${deferral.country ? ` in ${deferral.country}` : ''}, runs ${until} (${timezone})`;
}
